import { Avatar, Box, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SportsSoccerOutlinedIcon from "@mui/icons-material/SportsSoccerOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";

const number = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const normalizeTeamName = (value) => (value || "")
  .toString()
  .trim()
  .normalize("NFKD")
  .replace(/\p{Diacritic}/gu, "")
  .replace(/\s+/g, " ")
  .toLowerCase();

export const resolveTeamCrest = (equipo, estadisticas, clasificacion, jornadas = []) => {
  const teamId = String(estadisticas?.idEquipo ?? equipo?.idEquipo ?? equipo?.id ?? "");
  const teamName = normalizeTeamName(equipo?.nombre || clasificacion?.nombre || estadisticas?.nombre);
  const matches = jornadas.flatMap((round) => round?.partidos || []);

  for (const match of matches) {
    const localId = String(match?.codEquipoLocal ?? match?.idEquipoLocal ?? "");
    const visitorId = String(match?.codEquipoVisitante ?? match?.idEquipoVisitante ?? "");

    if (teamId && localId === teamId && match?.escudoEquipoLocal) return match.escudoEquipoLocal;
    if (teamId && visitorId === teamId && match?.escudoEquipoVisitante) return match.escudoEquipoVisitante;
  }

  for (const match of matches) {
    if (teamName && normalizeTeamName(match?.equipoLocal) === teamName && match?.escudoEquipoLocal) {
      return match.escudoEquipoLocal;
    }
    if (teamName && normalizeTeamName(match?.equipoVisitante) === teamName && match?.escudoEquipoVisitante) {
      return match.escudoEquipoVisitante;
    }
  }

  return clasificacion?.escudoEquipo || equipo?.escudo || equipo?.escudoEquipo;
};

export const buildTeamKpis = (clasificacion, estadisticas) => {
  const played = number(clasificacion?.jugados ?? estadisticas?.partidos?.partidosJugados);
  const wins = number(clasificacion?.ganados);
  const goalsFor = number(clasificacion?.golesFavor ?? estadisticas?.datos_extra?.sumaGolesMetidosTotal);
  const goalsAgainst = number(clasificacion?.golesContra ?? estadisticas?.datos_extra?.sumaGolesRecibidosTotal);
  const hasGoalData = clasificacion?.golesFavor != null || clasificacion?.golesContra != null || estadisticas?.datos_extra?.sumaGolesMetidosTotal != null || estadisticas?.datos_extra?.sumaGolesRecibidosTotal != null;
  return [
    { label: "Posición", value: clasificacion?.posicion ?? "—", icon: WorkspacePremiumOutlinedIcon },
    { label: "Puntos", value: clasificacion?.puntos ?? wins * 3 + number(clasificacion?.empatados), icon: EmojiEventsOutlinedIcon },
    { label: "Victorias", value: wins, helper: played ? `${Math.round((wins / played) * 100)}%` : "—", icon: TimelineOutlinedIcon },
    { label: "Diferencia de gol", value: hasGoalData ? goalsFor - goalsAgainst : "—", helper: hasGoalData ? `${goalsFor}:${goalsAgainst}` : "—", icon: SportsSoccerOutlinedIcon },
  ];
};

const EquipoExecutiveHeader = ({ equipo, estadisticas, clasificacion, jornadas, temporada, competicion }) => {
  const name = equipo?.nombre || clasificacion?.nombre || estadisticas?.nombre || "Equipo";
  const crest = resolveTeamCrest(equipo, estadisticas, clasificacion, jornadas);
  const kpis = buildTeamKpis(clasificacion, estadisticas);

  return (
    <Paper data-testid="team-executive-header" component="section" aria-labelledby="team-name" elevation={0} sx={{ position: "relative", overflow: "hidden", p: { xs: 2, sm: 3 }, border: 1, borderColor: "divider", borderRadius: 3, bgcolor: "background.paper", backgroundImage: (theme) => `linear-gradient(125deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1)}, transparent 58%)` }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "stretch", md: "center" }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: { md: "0 1 360px" } }}>
          <Avatar src={crest} alt={crest ? `Escudo de ${name}` : ""} sx={{ width: { xs: 72, sm: 88 }, height: { xs: 72, sm: 88 }, bgcolor: "background.default", border: 1, borderColor: "divider", p: 0.75, "& img": { objectFit: "contain" } }}>{name.charAt(0)}</Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography id="team-name" variant="overline" color="primary.main" fontWeight={800}>Informe ejecutivo</Typography>
            <Typography variant="h4" component="h1" fontWeight={800} sx={{ lineHeight: 1.1, overflowWrap: "anywhere" }}>{name}</Typography>
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {competicion && <Chip size="small" label={competicion} />}
              {temporada && <Chip size="small" variant="outlined" label={`Temporada ${temporada}`} />}
            </Stack>
          </Box>
        </Stack>
        <Grid container spacing={1.5} sx={{ flex: 1 }}>
          {kpis.map(({ label, value, helper, icon: Icon }) => (
            <Grid item xs={6} sm={3} key={label}>
              <Box sx={{ height: "100%", p: 1.5, borderRadius: 2, border: 1, borderColor: "divider", bgcolor: (theme) => alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.7 : 0.78) }}>
                <Stack direction="row" spacing={0.75} alignItems="center"><Icon sx={{ color: "primary.main", fontSize: 18 }} /><Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography></Stack>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>{value}</Typography>
                {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Paper>
  );
};

export default EquipoExecutiveHeader;
